// Wall Designer (React port). Single screen with three internal views:
// panels list -> panel editor (buttons) -> button editor (+ action sequence).
// Persists via wallApi.js. Colors use the native <input type="color">.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, IconButton, Typography, Button, List, ListItemButton,
  ListItemText, TextField, Slider, MenuItem, Select, Card, Stack, Divider, Fab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useApp } from '../../../../core/state/AppContext';
import { getDevicesByUser } from '../../../../core/api/api';
import {
  fetchPanels, createPanel, editPanel, removePanel, saveButton, removeButton,
} from './wallApi';

export default function WallDesignerScreen() {
  const { userId } = useApp();
  const navigate = useNavigate();
  const [panels, setPanels] = useState([]);
  const [devices, setDevices] = useState([]);
  const [view, setView] = useState('panels'); // panels | panel | button
  const [activePanel, setActivePanel] = useState(null);
  const [activeButton, setActiveButton] = useState(null);

  const load = useCallback(async () => setPanels(await fetchPanels(userId)), [userId]);

  useEffect(() => {
    load();
    getDevicesByUser(userId).then((r) => setDevices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [load, userId]);

  const back = () => {
    if (view === 'button') { setView('panel'); load(); }
    else if (view === 'panel') { setView('panels'); load(); }
    else navigate('/wall');
  };

  // ---------- Panels view ----------
  const addPanel = async () => {
    await createPanel(userId, { PanelName: `Panel ${panels.length + 1}`, SortOrder: panels.length });
    load();
  };

  // ---------- render ----------
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={back}><ArrowBackIcon /></IconButton>
          <Typography variant="h6">
            {view === 'panels' ? 'Wall Designer' : view === 'panel' ? 'Edit Panel' : 'Edit Button'}
          </Typography>
        </Toolbar>
      </AppBar>

      {view === 'panels' && (
        <>
          <List>
            {panels.map((p) => (
              <ListItemButton key={p.WallPanelId} onClick={() => { setActivePanel(p); setView('panel'); }}>
                <ListItemText primary={p.PanelName}
                  secondary={`${p.Buttons.length} buttons · ${p.ColumnsCount} cols`}
                  primaryTypographyProps={{ color: 'text.primary' }}
                  secondaryTypographyProps={{ color: 'text.secondary' }} />
                <IconButton onClick={(e) => { e.stopPropagation(); removePanel(p).then(load); }}>
                  <DeleteIcon color="error" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
          <Fab color="primary" variant="extended" sx={{ position: 'fixed', bottom: 24, right: 24 }} onClick={addPanel}>
            <AddIcon sx={{ mr: 1 }} /> Add Panel
          </Fab>
        </>
      )}

      {view === 'panel' && activePanel && (
        <PanelEditor
          panel={activePanel} panels={panels}
          onEditButton={(b) => { setActiveButton(b); setView('button'); }}
          onChanged={load}
        />
      )}

      {view === 'button' && activeButton && (
        <ButtonEditor
          userId={userId} button={activeButton} panels={panels} devices={devices}
          onDone={() => { setView('panel'); load(); }}
        />
      )}
    </Box>
  );
}

function PanelEditor({ panel, panels, onEditButton, onChanged }) {
  const [name, setName] = useState(panel.PanelName);
  const [cols, setCols] = useState(panel.ColumnsCount);
  const [back, setBack] = useState(panel.BackColor || '#101820');

  const save = () => editPanel(panel.WallPanelId, { PanelName: name, ColumnsCount: cols, BackColor: back }).then(onChanged);

  const newButton = () => onEditButton({
    WallPanelId: panel.WallPanelId, ButtonText: 'Button', ButtonType: 'action',
    ButtonColor: '#37474F', TextColor: '#FFFFFF', Width: 1, Height: 1,
    SortOrder: panel.Buttons.length, Actions: [],
  });

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <TextField label="Panel name" value={name} onChange={(e) => setName(e.target.value)} />
        <Box>
          <Typography color="text.primary">Columns: {cols}</Typography>
          <Slider min={1} max={8} value={cols} onChange={(_, v) => setCols(v)} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography color="text.primary">Background</Typography>
          <input type="color" value={back} onChange={(e) => setBack(e.target.value)} />
        </Box>
        <Button variant="contained" onClick={save}>Save Panel</Button>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" color="text.primary">Buttons</Typography>
          <Button startIcon={<AddIcon />} onClick={newButton} sx={{ color: 'text.primary' }}>Add Button</Button>
        </Box>
        {panel.Buttons.map((b) => (
          <Card key={b.WallButtonId} sx={{ bgcolor: b.ButtonColor, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, color: b.TextColor }}>
                <Typography fontWeight={600}>{b.ButtonText || '(no label)'}</Typography>
                <Typography variant="caption">
                  {b.ButtonType === 'navigate' ? 'Navigate' : `${b.Actions.length} steps`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => onEditButton(b)} sx={{ color: b.TextColor }}>Edit</IconButton>
              <IconButton size="small" onClick={() => removeButton(b.WallButtonId).then(onChanged)} sx={{ color: b.TextColor }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

function ButtonEditor({ userId, button, panels, devices, onDone }) {
  const [text, setText] = useState(button.ButtonText || '');
  const [btnColor, setBtnColor] = useState(button.ButtonColor || '#37474F');
  const [textColor, setTextColor] = useState(button.TextColor || '#FFFFFF');
  const [width, setWidth] = useState(button.Width || 1);
  const [height, setHeight] = useState(button.Height || 1);
  const [type, setType] = useState(button.ButtonType || 'action');
  const [target, setTarget] = useState(button.TargetWallPanelId || 0);
  const [actions, setActions] = useState(button.Actions || []);

  const addStep = (kind) => setActions((a) => [...a, kind === 'delay'
    ? { ActionType: 'delay', DelaySeconds: 2, StepOrder: a.length + 1 }
    : { ActionType: 'mqtt', UniqueNumber: '', MqttMessage: '', StepOrder: a.length + 1 }]);

  const update = (i, patch) => setActions((a) => a.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => setActions((a) => a.filter((_, idx) => idx !== i));
  const move = (i, dir) => setActions((a) => {
    const j = i + dir; if (j < 0 || j >= a.length) return a;
    const copy = [...a]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });

  const save = async () => {
    await saveButton(userId, {
      ...button, ButtonText: text, ButtonColor: btnColor, TextColor: textColor,
      Width: width, Height: height, ButtonType: type, TargetWallPanelId: target, Actions: actions,
    });
    onDone();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: 140, height: 64, bgcolor: btnColor, color: textColor, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
            {text || 'Preview'}
          </Box>
        </Box>
        <TextField label="Button text" value={text} onChange={(e) => setText(e.target.value)} />
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography color="text.primary">Button</Typography>
            <input type="color" value={btnColor} onChange={(e) => setBtnColor(e.target.value)} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography color="text.primary">Text</Typography>
            <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
          </Box>
        </Box>
        <Box><Typography color="text.primary">Width: {width}</Typography>
          <Slider min={1} max={4} value={width} onChange={(_, v) => setWidth(v)} /></Box>
        <Box><Typography color="text.primary">Height: {height}</Typography>
          <Slider min={1} max={4} value={height} onChange={(_, v) => setHeight(v)} /></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography color="text.primary">Type</Typography>
          <Select size="small" value={type} onChange={(e) => setType(e.target.value)}>
            <MenuItem value="action">Action sequence</MenuItem>
            <MenuItem value="navigate">Go to panel</MenuItem>
          </Select>
        </Box>

        {type === 'navigate' && (
          <Select size="small" value={target} onChange={(e) => setTarget(e.target.value)} displayEmpty>
            <MenuItem value={0}>Select target panel</MenuItem>
            {panels.map((p) => <MenuItem key={p.WallPanelId} value={p.WallPanelId}>{p.PanelName}</MenuItem>)}
          </Select>
        )}

        {type === 'action' && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" color="text.primary">Action sequence</Typography>
              <Box>
                <Button size="small" onClick={() => addStep('mqtt')} sx={{ color: 'text.primary' }}>+ MQTT</Button>
                <Button size="small" onClick={() => addStep('delay')} sx={{ color: 'text.primary' }}>+ Delay</Button>
              </Box>
            </Box>
            {actions.map((s, i) => (
              <Card key={i} sx={{ bgcolor: 'primary.main', p: 1.5 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ flex: 1, color: 'primary.contrastText' }}>Step {i + 1} — {s.ActionType}</Typography>
                    <IconButton size="small" sx={{ color: 'primary.contrastText' }} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" sx={{ color: 'primary.contrastText' }} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" sx={{ color: 'primary.contrastText' }} onClick={() => remove(i)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                  {s.ActionType === 'mqtt' ? (
                    <>
                      <Select size="small" value={s.UniqueNumber || ''} displayEmpty
                        onChange={(e) => update(i, { UniqueNumber: e.target.value })}>
                        <MenuItem value="">Select device</MenuItem>
                        {devices.map((d) => (
                          <MenuItem key={d.UniqueNumber} value={String(d.UniqueNumber)}>
                            {(d.DeviceName || d.UniqueNumber)} ({d.UniqueNumber})
                          </MenuItem>
                        ))}
                      </Select>
                      <TextField size="small" label="MQTT message (e.g. 1-1, 1-0, _100)"
                        value={s.MqttMessage || ''} onChange={(e) => update(i, { MqttMessage: e.target.value })} />
                    </>
                  ) : (
                    <TextField size="small" type="number" label="Delay (seconds)"
                      value={s.DelaySeconds} onChange={(e) => update(i, { DelaySeconds: Number(e.target.value) })} />
                  )}
                </Stack>
              </Card>
            ))}
          </>
        )}

        <Button variant="contained" onClick={save}>Save Button</Button>
      </Stack>
    </Box>
  );
}
